/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\IVRGeneralTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Incoming Call Tools module.
----------------------------------------------------------------*/

import React from 'react';
import { Phone, Settings, ListChecks, Plus } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import RecordingSelect from '../../../components/ui/RecordingSelect';
import { useI18n } from '../../../hooks/useI18n';
import { IVR, IVROption, IVRSettings } from '../types/ivr.types';
import { OptionEditor } from '../components/IVROptionEditor';
import { DestinationSelector } from '../components/IVROptionEditor';

interface IVRFormData {
  name: string;
  context: string;
  welcomeMessage: string;
  instructionsMessage: string;
  invalidMessage: string;
  exitMessage: string;
  timeout: number;
  maxFailures: number;
  maxTimeouts: number;
  directDial: boolean;
  enabled: boolean;
  statsEnabled: boolean;
  settings: IVRSettings;
  options: IVROption[];
}

interface IVRGeneralTabProps {
  formData: IVRFormData;
  setFormData: React.Dispatch<React.SetStateAction<IVRFormData>>;
  errors: Record<string, string>;
  availableIVRs: IVR[];
  editingOptionIndex: number | null;
  setEditingOptionIndex: (index: number | null) => void;
  onAddOption: () => void;
  onUpdateOption: (index: number, option: IVROption) => void;
  onDeleteOption: (index: number) => void;
  onSubmit: (e?: React.FormEvent) => void;
  generateContextFromName: (name: string) => string;
}

export function IVRGeneralTab({
  formData, setFormData, errors, availableIVRs, editingOptionIndex, setEditingOptionIndex,
  onAddOption, onUpdateOption, onDeleteOption, onSubmit, generateContextFromName
}: IVRGeneralTabProps) {
  const { t } = useI18n();

  const update = (field: keyof IVRFormData, value: unknown) => setFormData(prev => ({ ...prev, [field]: value }));

  return (
    <form onSubmit={onSubmit}>
      <FormBox title={t('ivrs:sections.general')} icon={<Phone className="h-5 w-5" />} className="mt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
          {/* Name */}
          <Label htmlFor="name">
            <SimpleTooltip content={t('ivrs:fields.name.tooltip')}><span>{t('ivrs:fields.name.label')}<span className="text-red-500 ml-1">*</span></span></SimpleTooltip>
          </Label>
          <div>
            <Input id="name" value={formData.name}
              onChange={(e) => { const newName = e.target.value; update('name', newName); setFormData(prev => ({ ...prev, name: newName, context: generateContextFromName(newName) })); }}
              placeholder={t('ivrs:fields.name.placeholder')} className={errors.name ? 'border-red-500' : ''} />
            {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
          </div>

          <Label htmlFor="timeout" className="col-start-3">
            <SimpleTooltip content={t('ivrs:fields.timeout.tooltip')}>{t('ivrs:fields.timeout.label')}<span className="text-red-500 ml-1">*</span></SimpleTooltip>
          </Label>
          <div>
            <Input id="timeout" type="number" min="1" max="60" value={formData.timeout}
              onChange={(e) => update('timeout', parseInt(e.target.value) || 5)} className={errors.timeout ? 'border-red-500' : ''} />
            {errors.timeout && <p className="text-sm text-red-500 mt-1">{errors.timeout}</p>}
          </div>

          <Label htmlFor="welcomeMessage" className="col-start-1">
            <SimpleTooltip content={t('ivrs:fields.welcomeMessage.tooltip')}>{t('ivrs:fields.welcomeMessage.label')}</SimpleTooltip>
          </Label>
          <div className="min-w-0">
            <RecordingSelect value={formData.welcomeMessage} onChange={(value) => update('welcomeMessage', value)} placeholder={t('ivrs:fields.welcomeMessage.placeholder')} returnType="id" />
          </div>

          <Label htmlFor="maxFailures" className="col-start-3">
            <SimpleTooltip content={t('ivrs:fields.maxFailures.tooltip')}>{t('ivrs:fields.maxFailures.label')}<span className="text-red-500 ml-1">*</span></SimpleTooltip>
          </Label>
          <div>
            <Input id="maxFailures" type="number" min="1" max="10" value={formData.maxFailures}
              onChange={(e) => update('maxFailures', parseInt(e.target.value) || 3)} className={errors.maxFailures ? 'border-red-500' : ''} />
            {errors.maxFailures && <p className="text-sm text-red-500 mt-1">{errors.maxFailures}</p>}
          </div>

          <Label htmlFor="instructionsMessage" className="col-start-1">
            <SimpleTooltip content={t('ivrs:fields.instructionsMessage.tooltip')}>{t('ivrs:fields.instructionsMessage.label')}</SimpleTooltip>
          </Label>
          <div className="min-w-0">
            <RecordingSelect value={formData.instructionsMessage} onChange={(value) => update('instructionsMessage', value)} placeholder={t('ivrs:fields.instructionsMessage.placeholder')} returnType="id" />
          </div>

          <Label htmlFor="maxTimeouts" className="col-start-3">
            <SimpleTooltip content={t('ivrs:fields.maxTimeouts.tooltip')}>{t('ivrs:fields.maxTimeouts.label')}<span className="text-red-500 ml-1">*</span></SimpleTooltip>
          </Label>
          <div>
            <Input id="maxTimeouts" type="number" min="1" max="10" value={formData.maxTimeouts}
              onChange={(e) => update('maxTimeouts', parseInt(e.target.value) || 3)} className={errors.maxTimeouts ? 'border-red-500' : ''} />
            {errors.maxTimeouts && <p className="text-sm text-red-500 mt-1">{errors.maxTimeouts}</p>}
          </div>

          <Label htmlFor="invalidMessage" className="col-start-1">
            <SimpleTooltip content={t('ivrs:fields.invalidMessage.tooltip')}>{t('ivrs:fields.invalidMessage.label')}</SimpleTooltip>
          </Label>
          <div className="min-w-0">
            <RecordingSelect value={formData.invalidMessage} onChange={(value) => update('invalidMessage', value)} placeholder={t('ivrs:fields.invalidMessage.placeholder')} returnType="id" />
          </div>

          <Label htmlFor="interDigitTimeout" className="col-start-3">
            <SimpleTooltip content={t('ivrs:fields.interDigitTimeout.tooltip')}>{t('ivrs:fields.interDigitTimeout.label')}<span className="text-red-500 ml-1">*</span></SimpleTooltip>
          </Label>
          <div>
            <Input id="interDigitTimeout" type="number" min="1000" max="10000" step="100" value={formData.settings.interDigitTimeout}
              onChange={(e) => setFormData(prev => ({ ...prev, settings: { ...prev.settings, interDigitTimeout: parseInt(e.target.value) || 5000 } }))}
              className={errors.interDigitTimeout ? 'border-red-500' : ''} />
            {errors.interDigitTimeout && <p className="text-sm text-red-500 mt-1">{errors.interDigitTimeout}</p>}
          </div>

          <Label htmlFor="digitTimeoutSound" className="col-start-1">
            <SimpleTooltip content={t('ivrs:fields.digitTimeoutSound.tooltip')}>{t('ivrs:fields.digitTimeoutSound.label')}</SimpleTooltip>
          </Label>
          <div className="min-w-0">
            <RecordingSelect value={formData.settings.digitTimeoutSound ?? ''} onChange={(value) => setFormData(prev => ({ ...prev, settings: { ...prev.settings, digitTimeoutSound: value } }))} placeholder={t('ivrs:fields.digitTimeoutSound.placeholder')} returnType="id" />
          </div>

          <Label htmlFor="exitMessage" className="col-start-3">
            <SimpleTooltip content={t('ivrs:fields.exitMessage.tooltip')}>{t('ivrs:fields.exitMessage.label')}</SimpleTooltip>
          </Label>
          <div className="min-w-0">
            <RecordingSelect value={formData.exitMessage} onChange={(value) => update('exitMessage', value)} placeholder={t('ivrs:fields.exitMessage.placeholder')} returnType="id" />
          </div>

          <Label className="col-start-1"><SimpleTooltip content={t('ivrs:fields.directDial.tooltip')}>{t('ivrs:fields.directDial.label')}</SimpleTooltip></Label>
          <div><Toggle value={formData.directDial} onChange={(checked) => update('directDial', checked)} /></div>

          <Label className="col-start-3"><SimpleTooltip content={t('ivrs:fields.statsEnabled.tooltip') || ''}>{t('ivrs:fields.statsEnabled.label') || 'Track Statistics'}</SimpleTooltip></Label>
          <div><Toggle value={formData.statsEnabled} onChange={(checked) => update('statsEnabled', checked)} /></div>

          <Label className="col-start-1"><SimpleTooltip content={t('ivrs:fields.enabled.tooltip')}>{t('ivrs:fields.enabled.label')}</SimpleTooltip></Label>
          <div><Toggle value={formData.enabled} onChange={(checked) => update('enabled', checked)} /></div>
        </div>
      </FormBox>

      <FormBox title={t('ivrs:sections.destinations')} icon={<Settings className="h-5 w-5" />} className="mt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
          <Label><SimpleTooltip content={t('ivrs:fields.timeoutDestination.tooltip')}>{t('ivrs:fields.timeoutDestination.label')}</SimpleTooltip></Label>
          <div className="col-span-3">
            <DestinationSelector value={formData.settings.timeoutDestination} onChange={(value) => setFormData(prev => ({ ...prev, settings: { ...prev.settings, timeoutDestination: value } }))} availableIVRs={availableIVRs} />
          </div>
          <Label><SimpleTooltip content={t('ivrs:fields.failureDestination.tooltip')}>{t('ivrs:fields.failureDestination.label')}</SimpleTooltip></Label>
          <div className="col-span-3">
            <DestinationSelector value={formData.settings.failureDestination} onChange={(value) => setFormData(prev => ({ ...prev, settings: { ...prev.settings, failureDestination: value } }))} availableIVRs={availableIVRs} />
          </div>
        </div>
      </FormBox>

      {/* Options preview inside General tab area � rendered in its own tab in the parent */}
      <FormBox title={t('ivrs:sections.ivrOptions')} icon={<ListChecks className="h-5 w-5" />} className="mt-6">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('ivrs:options.description')}</p>
          {formData.options.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ListChecks className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('ivrs:options.empty')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-4 items-center pb-2 border-b font-semibold text-sm">
                <div className="col-span-2">{t('ivrs:options.header.digits')}</div>
                <div className="col-span-8">{t('ivrs:options.header.destination')}</div>
                <div className="col-span-1 text-center">{t('ivrs:options.header.enabled')}</div>
                <div className="col-span-1"></div>
              </div>
              {formData.options.map((option, index) => (
                <OptionEditor key={index} option={option} index={index} isEditing={editingOptionIndex === index}
                  onEdit={() => setEditingOptionIndex(index)} onCancel={() => setEditingOptionIndex(null)}
                  onSave={(updated) => onUpdateOption(index, updated)} onDelete={() => onDeleteOption(index)}
                  availableIVRs={availableIVRs} errors={errors} />
              ))}
            </div>
          )}
          <div className="flex justify-end pt-4 border-t">
            <button type="button" onClick={onAddOption}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
              <Plus className="h-4 w-4" />{t('ivrs:actions.add')}
            </button>
          </div>
        </div>
      </FormBox>
    </form>
  );
}
